import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompany } from '@/contexts/CompanyContext';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  Filter,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
  Receipt,
  User,
  Maximize2,
  Minimize2,
  FileText,
  Settings2,
} from 'lucide-react';
import type { Expense } from '@/types';
import { format } from 'date-fns';
import { ApprovalHistoryTimeline } from '../time/approval-history-timeline';

// DataTable + pagination
import { DataTableEnhanced, type ColumnDef, type ColumnSort, columnDefinitionsToVisibility } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray } from '@/types/pagination';
import type { PaginationMetadata } from '@/types/pagination';

// Column configurator + preferences
import { ColumnConfiguratorDrawer, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';

// Advanced filter sheet
import {
  ExpenseEntriesFilterSheet,
  type ExpenseFilters,
  countActiveExpenseFilters,
  applyExpenseFilters,
} from './expense-entries-filter-sheet';

interface ExpenseEntriesTabProps {
  projectId: number;
  expenses: Expense[];
  employees: Array<{ id: number; name: string }>;
}

interface ExpenseSummary {
  totalAmount: number;
  billableAmount: number;
  nonBillableAmount: number;
  approvedAmount: number;
  pendingAmount: number;
  reimbursableAmount: number;
  uniqueEmployees: number;
}

// ─── Column definitions ────────────────────────────────────────────────────────
const DEFAULT_EXPENSE_COLUMNS: ColumnDefinition[] = [
  { id: 'date',        name: 'Date',        description: 'Expense date',             category: 'Timeline',  visible: true, locked: true, order: 0 },
  { id: 'employee',    name: 'Employee',    description: 'Employee name',            category: 'Resource',  visible: true, locked: true, order: 1 },
  { id: 'category',    name: 'Category',    description: 'Expense category',         category: 'Status',    visible: true, order: 2 },
  { id: 'description', name: 'Description', description: 'Expense description',      category: 'Status',    visible: true, order: 3 },
  { id: 'amount',      name: 'Amount',      description: 'Expense amount',           category: 'Financial', visible: true, locked: true, order: 4 },
  { id: 'billable',    name: 'Billable',    description: 'Whether expense is billable', category: 'Financial', visible: true, order: 5 },
  { id: 'status',      name: 'Status',      description: 'Approval status',          category: 'Status',    visible: true, locked: true, order: 6 },
  { id: 'receipt',     name: 'Receipt',     description: 'Receipt attachment',       category: 'Status',    visible: true, order: 7 },
];

export function ExpenseEntriesTab({
  projectId: _projectId,
  expenses,
  employees,
}: ExpenseEntriesTabProps) {
  const { currentCompany } = useCompany();

  // ── UI state ──────────────────────────────────────────────────────────
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [approvalHistorySheetOpen, setApprovalHistorySheetOpen] = useState(false);
  const [approvalHistoryExpense, setApprovalHistoryExpense] = useState<Expense | null>(null);
  const [approvalHistoryExpanded, setApprovalHistoryExpanded] = useState(false);

  // ── Search / filter state ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState<ExpenseFilters>({});
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // ── Table state ───────────────────────────────────────────────────────
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([{ columnId: 'date', direction: 'desc' }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // ── Column preferences ────────────────────────────────────────────────
  const { columns: expenseColumns, updateColumns } = useColumnPreferences({
    storageKey: 'expense-entries-columns',
    defaultColumns: DEFAULT_EXPENSE_COLUMNS,
  });

  const visibleColumns = useMemo(
    () => expenseColumns.filter((c) => c.visible).sort((a, b) => a.order - b.order),
    [expenseColumns]
  );

  const getEmployeeName = (expense: Expense): string => {
    if (expense.employeeName) return expense.employeeName;
    const employee = employees.find((e) => e.id === expense.employeeId);
    return employee?.name || 'Unknown Employee';
  };

  // ── Derived option lists ──────────────────────────────────────────────
  const availableEmployees = useMemo(() => {
    const ids = Array.from(new Set(expenses.map((e) => e.employeeId)));
    return ids
      .map((id) => {
        const ex = expenses.find((e) => e.employeeId === id);
        return { value: String(id), label: ex ? getEmployeeName(ex) : 'Unknown Employee' };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [expenses, employees]);

  const availableCategories = useMemo(() => {
    const cats = Array.from(new Set(expenses.map((e) => e.category))).sort();
    return cats.map((c) => ({ value: c, label: c }));
  }, [expenses]);

  // ── Summary ───────────────────────────────────────────────────────────
  const summary: ExpenseSummary = useMemo(() => {
    const totalAmount = expenses.reduce((s, e) => s + e.amount, 0);
    const billableAmount = expenses.filter((e) => e.billable).reduce((s, e) => s + e.amount, 0);
    const approvedAmount = expenses.filter((e) => e.approved).reduce((s, e) => s + e.amount, 0);
    return {
      totalAmount,
      billableAmount,
      nonBillableAmount: totalAmount - billableAmount,
      approvedAmount,
      pendingAmount: totalAmount - approvedAmount,
      reimbursableAmount: expenses.filter((e) => e.reimbursable).reduce((s, e) => s + e.amount, 0),
      uniqueEmployees: new Set(expenses.map((e) => e.employeeId)).size,
    };
  }, [expenses]);

  // ── Filter + sort + paginate ──────────────────────────────────────────
  const filtered = useMemo(() => {
    let result = [...expenses];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.description.toLowerCase().includes(q) ||
          e.category.toLowerCase().includes(q) ||
          getEmployeeName(e).toLowerCase().includes(q) ||
          e.date.includes(q) ||
          (e.merchantName && e.merchantName.toLowerCase().includes(q))
      );
    }
    result = applyExpenseFilters(result, advancedFilters);
    return result;
  }, [expenses, searchQuery, advancedFilters]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    for (const sort of [...columnSorts].reverse()) {
      result.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;
        switch (sort.columnId) {
          case 'date':     aVal = new Date(a.date).getTime(); bVal = new Date(b.date).getTime(); break;
          case 'employee': aVal = getEmployeeName(a); bVal = getEmployeeName(b); break;
          case 'amount':   aVal = a.amount; bVal = b.amount; break;
          case 'category': aVal = a.category; bVal = b.category; break;
          default:         aVal = String((a as unknown as Record<string, unknown>)[sort.columnId] ?? ''); bVal = String((b as unknown as Record<string, unknown>)[sort.columnId] ?? '');
        }
        const cmp = typeof aVal === 'number' && typeof bVal === 'number'
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));
        return sort.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [filtered, columnSorts]);

  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(sorted.length, currentPage, itemsPerPage),
    [sorted.length, currentPage, itemsPerPage]
  );

  const paginatedExpenses = useMemo(
    () => paginateArray(sorted, currentPage, itemsPerPage).data,
    [sorted, currentPage, itemsPerPage]
  );

  useEffect(() => { setCurrentPage(1); }, [searchQuery, advancedFilters, columnSorts, itemsPerPage]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

  // ── Column defs ───────────────────────────────────────────────────────
  const columnDefs = useMemo((): ColumnDef<Expense, unknown>[] => {
    const cols: ColumnDef<Expense, unknown>[] = [];
    const vis = columnDefinitionsToVisibility(visibleColumns);

    if (vis['date']) cols.push({
      id: 'date',
      header: 'Date',
      accessorFn: (r) => r.date,
      cell: ({ row }) => (
        <button
          onClick={() => setSelectedExpense(row.original)}
          className="text-primary hover:underline focus:outline-none rounded font-medium"
        >
          {format(new Date(row.original.date), 'dd MMM yyyy')}
        </button>
      ),
    });

    if (vis['employee']) cols.push({
      id: 'employee',
      header: 'Employee',
      accessorFn: (r) => getEmployeeName(r),
    });

    if (vis['category']) cols.push({
      id: 'category',
      header: 'Category',
      accessorFn: (r) => r.category,
      cell: ({ row }) => <Badge variant="outline">{row.original.category}</Badge>,
    });

    if (vis['description']) cols.push({
      id: 'description',
      header: 'Description',
      accessorFn: (r) => r.description,
      cell: ({ row }) => (
        <span className="max-w-[300px] truncate block">{row.original.description}</span>
      ),
    });

    if (vis['amount']) cols.push({
      id: 'amount',
      header: 'Amount',
      accessorFn: (r) => r.amount,
      cell: ({ row }) => <span className="font-medium">{formatCurrency(row.original.amount)}</span>,
      meta: { align: 'right' },
    });

    if (vis['billable']) cols.push({
      id: 'billable',
      header: 'Billable',
      enableSorting: false,
      accessorFn: (r) => r.billable ? 'Billable' : 'Non-Billable',
      cell: ({ row }) => row.original.billable ? (
        <Badge variant="default" accent="info" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />Billable
        </Badge>
      ) : (
        <Badge variant="secondary" className="gap-1">
          <XCircle className="h-3 w-3" />Non-Billable
        </Badge>
      ),
    });

    if (vis['status']) cols.push({
      id: 'status',
      header: 'Status',
      enableSorting: false,
      accessorFn: (r) => r.approved ? 'Approved' : 'Pending',
      cell: ({ row }) => {
        const expense = row.original;
        return expense.approved ? (
          <Badge
            variant="success"
            accent="success"
            className="gap-1 cursor-pointer hover:opacity-80"
            onClick={(e) => { e.stopPropagation(); setApprovalHistoryExpense(expense); setApprovalHistorySheetOpen(true); }}
          >
            <CheckCircle2 className="h-3 w-3" />Approved
          </Badge>
        ) : (
          <Badge
            variant="warning"
            accent="warning"
            className="gap-1 cursor-pointer hover:opacity-80"
            onClick={(e) => { e.stopPropagation(); setApprovalHistoryExpense(expense); setApprovalHistorySheetOpen(true); }}
          >
            <Clock className="h-3 w-3" />Pending
          </Badge>
        );
      },
    });

    if (vis['receipt']) cols.push({
      id: 'receipt',
      header: 'Receipt',
      enableSorting: false,
      accessorFn: (r) => (r.receiptUrl || r.attachments?.length) ? 'Attached' : 'None',
      cell: ({ row }) => {
        const expense = row.original;
        return (expense.receiptUrl || (expense.attachments && expense.attachments.length > 0)) ? (
          <Badge variant="outline" className="gap-1">
            <FileText className="h-3 w-3" />Attached
          </Badge>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        );
      },
    });

    return cols;
  }, [visibleColumns, expenses, employees]);

  // ── Early returns ─────────────────────────────────────────────────────
  if (expenses.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-muted p-3">
                <Receipt className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">No Expenses</h3>
                <p className="text-sm text-muted-foreground">
                  There are no expenses associated with this project yet.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeFilterCount = countActiveExpenseFilters(advancedFilters);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.totalAmount)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {expenses.length} expense{expenses.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Billable Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{formatCurrency(summary.billableAmount)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.totalAmount > 0 ? ((summary.billableAmount / summary.totalAmount) * 100).toFixed(0) : 0}% of total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Approved Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <div className="text-2xl font-bold">{formatCurrency(summary.approvedAmount)}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.totalAmount > 0 ? ((summary.approvedAmount / summary.totalAmount) * 100).toFixed(0) : 0}% approved
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Team Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div className="text-2xl font-bold">{summary.uniqueEmployees}</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Submitting expenses</p>
          </CardContent>
        </Card>
      </div>

      {/* Table Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Expense Entries</CardTitle>
              <CardDescription>
                Expenses submitted for this project{currentCompany ? ` from ${currentCompany.name}` : ''}
              </CardDescription>
            </div>
            {/* Toolbar */}
            <div className="flex items-center gap-2">
              <div className="relative w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search expenses..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 relative"
                onClick={() => setFilterSheetOpen(true)}
                title="Advanced filters"
              >
                <Filter className="h-3.5 w-3.5" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center leading-none">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setColumnConfigOpen(true)}
                title="Configure columns"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <DataTableEnhanced
            data={paginatedExpenses}
            columns={columnDefs}
            columnVisibility={columnDefinitionsToVisibility(visibleColumns)}
            columnSorts={columnSorts}
            onColumnSortChange={(newSorts) => { setColumnSorts(newSorts); setCurrentPage(1); }}
            emptyMessage={
              <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                <Receipt className="h-8 w-8" />
                <p>No expenses match your filters</p>
              </div>
            }
            paginationContent={
              <PaginationControls
                pagination={pagination}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
              />
            }
          />
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={!!selectedExpense} onOpenChange={(open) => { if (!open) setSelectedExpense(null); }}>
        <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
          {selectedExpense && (
            <>
              <SheetHeader className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5" />
                      <SheetTitle>Expense Details</SheetTitle>
                    </div>
                    <SheetDescription className="space-y-1">
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(selectedExpense.date), 'EEEE, dd MMMM yyyy')}
                      </div>
                      <div className="text-xs">
                        Internal ID: <span className="font-mono font-medium">{selectedExpense.id}</span>
                      </div>
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                <div className="flex gap-2 flex-wrap">
                  {selectedExpense.billable ? (
                    <Badge variant="default" accent="info" className="gap-1"><CheckCircle2 className="h-3 w-3" />Billable</Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1"><XCircle className="h-3 w-3" />Non-Billable</Badge>
                  )}
                  {selectedExpense.approved ? (
                    <Badge variant="success" accent="success" className="gap-1"><CheckCircle2 className="h-3 w-3" />Approved</Badge>
                  ) : (
                    <Badge variant="warning" accent="warning" className="gap-1"><Clock className="h-3 w-3" />Pending Approval</Badge>
                  )}
                  {selectedExpense.reimbursable && (
                    <Badge variant="outline" className="gap-1"><DollarSign className="h-3 w-3" />Reimbursable</Badge>
                  )}
                  {selectedExpense.policyViolation && (
                    <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" />Policy Violation</Badge>
                  )}
                </div>
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Employee</label>
                    <p className="mt-1 text-sm font-medium">{getEmployeeName(selectedExpense)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="mt-1 text-sm">{selectedExpense.description}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Amount</label>
                      <p className="mt-1 text-lg font-semibold">{formatCurrency(selectedExpense.amount)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Date</label>
                      <p className="mt-1 text-sm">{format(new Date(selectedExpense.date), 'dd MMM yyyy')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Category</label>
                      <p className="mt-1"><Badge variant="outline">{selectedExpense.category}</Badge></p>
                    </div>
                    {selectedExpense.merchantName && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Merchant</label>
                        <p className="mt-1 text-sm">{selectedExpense.merchantName}</p>
                      </div>
                    )}
                  </div>
                  {selectedExpense.policyViolation && selectedExpense.policyViolationReason && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <label className="text-sm font-medium text-destructive">Policy Violation</label>
                      <p className="mt-1 text-sm text-destructive">{selectedExpense.policyViolationReason}</p>
                    </div>
                  )}
                  {selectedExpense.reviewerComment && (
                    <div className="p-3 bg-muted rounded-lg">
                      <label className="text-sm font-medium text-muted-foreground">Reviewer Comment</label>
                      <p className="mt-1 text-sm">{selectedExpense.reviewerComment}</p>
                    </div>
                  )}
                  {(selectedExpense.receiptUrl || (selectedExpense.attachments && selectedExpense.attachments.length > 0)) && (
                    <div className="pt-4 border-t">
                      <h4 className="text-sm font-medium mb-3">Attachments</h4>
                      <div className="space-y-2">
                        {selectedExpense.receiptUrl && (
                          <Button variant="outline" size="sm" className="w-full justify-start" asChild>
                            <a href={selectedExpense.receiptUrl} target="_blank" rel="noopener noreferrer">
                              <FileText className="h-4 w-4 mr-2" />View Receipt<ExternalLink className="h-3 w-3 ml-auto" />
                            </a>
                          </Button>
                        )}
                        {selectedExpense.attachments?.map((attachment) => (
                          <Button key={attachment.id} variant="outline" size="sm" className="w-full justify-start" asChild>
                            <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                              <FileText className="h-4 w-4 mr-2" />{attachment.fileName}<ExternalLink className="h-3 w-3 ml-auto" />
                            </a>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {selectedExpense.externalUrl && (
                  <div className="pt-4 border-t">
                    <Button className="w-full" variant="outline" asChild>
                      <a href={selectedExpense.externalUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                        <ExternalLink className="h-4 w-4" />View in Expense System
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Approval History Sheet / Dialog */}
      {approvalHistoryExpanded ? (
        <Dialog open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>Approval History for Expense</DialogTitle>
                  <DialogDescription>
                    {approvalHistoryExpense && (
                      <>{format(new Date(approvalHistoryExpense.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryExpense)}</>
                    )}
                  </DialogDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setApprovalHistoryExpanded(false)} title="Return to sheet view">
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryExpense?.approvalHistory && (
                <ApprovalHistoryTimeline approvalHistory={approvalHistoryExpense.approvalHistory} timeEntryId={String(approvalHistoryExpense.id)} />
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={approvalHistorySheetOpen} onOpenChange={setApprovalHistorySheetOpen}>
          <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
            <SheetHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>Approval History for Expense</SheetTitle>
                  <SheetDescription>
                    {approvalHistoryExpense && (
                      <>{format(new Date(approvalHistoryExpense.date), 'EEEE, dd MMMM yyyy')} - {getEmployeeName(approvalHistoryExpense)}</>
                    )}
                  </SheetDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setApprovalHistoryExpanded(true)} title="Expand to full view">
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {approvalHistoryExpense?.approvalHistory && (
                <ApprovalHistoryTimeline approvalHistory={approvalHistoryExpense.approvalHistory} timeEntryId={String(approvalHistoryExpense.id)} />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Column Configurator */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={expenseColumns}
        onSave={updateColumns}
        pageKey="expense-entries"
      />

      {/* Advanced Filter Sheet */}
      <ExpenseEntriesFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={advancedFilters}
        onApply={(f) => { setAdvancedFilters(f); setCurrentPage(1); }}
        onClear={() => { setAdvancedFilters({}); setCurrentPage(1); }}
        availableEmployees={availableEmployees}
        availableCategories={availableCategories}
      />
    </div>
  );
}
